import { useEffect, useState } from "react";
import { PaperclipIcon, DownloadIcon, TrashIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import { FileUpload } from "@/components/Common/FileUpload";
import { useUserQuery } from "@/hooks/queries/useUserQuery";
import { toast } from "@/hooks/useToast";
import { formatBytes, firestoreToDateTime } from "@/lib/utils";
import { createId } from "@/lib/database";
import { storageActions } from "@/services/storage";
import { attachmentRepository } from "@/repositories/attachment";
import { Attachment } from "@/types/v2";
import { format } from "date-fns";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const SOFT_CAP_COUNT = 5;

// Firebase Storage object paths treat "/" as a path separator, so a filename
// containing one (or other characters that need escaping in a path segment)
// would silently create nested "folders" instead of a single object.
function sanitizeFileName(fileName: string): string {
  return fileName.replace(/[^\w.-]+/g, "_");
}

export interface AttachmentsMainProps {
  shopId: string;
  entityPath: string;
  attachmentsPath: string;
  canModify: boolean;
}

const AttachmentRow = ({
  attachment,
  canModify,
  onDelete,
}: {
  attachment: Attachment;
  canModify: boolean;
  onDelete: (id: string) => void;
}) => {
  const { data: uploader } = useUserQuery(attachment.createdBy ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const uploadedAt = firestoreToDateTime(attachment.createdAt);

  const handleDownload = async () => {
    try {
      const url = await storageActions.getDownloadURL(attachment.storagePath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to download attachment:", error);
      toast({
        title: "Download failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border p-2">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">
          {attachment.fileName}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatBytes(attachment.size)}
          {uploader?.displayName && ` · ${uploader.displayName}`}
          {uploadedAt && ` · ${format(uploadedAt, "MMM d, yyyy")}`}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Download ${attachment.fileName}`}
          onClick={handleDownload}
        >
          <DownloadIcon className="h-4 w-4" />
        </Button>
        {canModify &&
          (confirmingDelete ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  onDelete(attachment.id);
                  setConfirmingDelete(false);
                }}
              >
                Delete
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete ${attachment.fileName}`}
              onClick={() => setConfirmingDelete(true)}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          ))}
      </div>
    </div>
  );
};

export const AttachmentsMain = ({
  shopId,
  entityPath,
  attachmentsPath,
  canModify,
}: AttachmentsMainProps) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(
    () => attachmentRepository.subscribe(attachmentsPath, setAttachments),
    [attachmentsPath]
  );

  const handleFileUpload = async (file: File) => {
    // >= (not >): storage.rules:17 enforces a strict `< 5 * 1024 * 1024`,
    // rejecting a file of exactly MAX_FILE_SIZE_BYTES server-side. This
    // client check must be at least as conservative or a boundary-sized
    // file passes here, fails the Storage rule, and surfaces as a generic
    // "Upload failed" toast instead of this clear "File too large" one.
    if (file.size >= MAX_FILE_SIZE_BYTES) {
      toast({
        title: "File too large",
        description: `${file.name} is over the 5MB limit.`,
        variant: "destructive",
      });
      return;
    }
    if (attachments.length >= SOFT_CAP_COUNT) {
      toast({
        title: "This entity already has 5+ attachments",
        description: "Uploading anyway — consider removing unused files.",
        variant: "default",
      });
    }

    setIsUploading(true);
    try {
      const attachmentId = createId();
      const storagePath = `shops/${shopId}/attachments/${entityPath.split("/").pop()}/${attachmentId}-${sanitizeFileName(file.name)}`;
      await storageActions.upload(file, storagePath);
      await attachmentRepository.create(entityPath, attachmentsPath, {
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        storagePath,
      });
    } catch (error) {
      console.error("Failed to upload attachment:", error);
      toast({
        title: "Upload failed",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (id: string) => {
    attachmentRepository
      .delete(entityPath, attachmentsPath, id)
      .catch((error) => {
        console.error("Failed to delete attachment:", error);
        toast({
          title: "Failed to delete attachment",
          description: "Please try again.",
          variant: "destructive",
        });
      });
  };

  return (
    <div className="flex w-full flex-col gap-3">
      {attachments.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
          <PaperclipIcon className="h-6 w-6" />
          <span className="text-sm">No attachments yet</span>
        </div>
      )}
      {attachments.map((attachment) => (
        <AttachmentRow
          key={attachment.id}
          attachment={attachment}
          canModify={canModify}
          onDelete={handleDelete}
        />
      ))}
      {canModify && (
        <FileUpload
          text={
            isUploading ? "Uploading..." : "Drop a file, or click to upload"
          }
          handleFileUpload={handleFileUpload}
        />
      )}
    </div>
  );
};
